import type {CSSProperties, ReactNode} from 'react'

import {useBoardInteraction} from '../provider/contexts'
import type {BoardCardAddress} from '../types'
import {DragOverlay} from './dnd-kit'

export type BoardDragOverlayProps = {
  /**
   * Draws the copy of the card that follows the pointer, and is called only while a board
   * card is being held.
   *
   * A render prop because this package never learns what an item is: the address names
   * which card is in hand, and the caller turns that into the same card it drew in the
   * cell. `@github-ui/board` resolves the item with `useBoardSelection().describeCard`
   * and hands it to its own `renderCard`.
   *
   * **What you return is hidden and made inert for you**, so return the card you drew in
   * the cell and change nothing about it. The real card has not left the document, so a
   * copy anything could read out would announce the same card twice, and a copy of a card
   * carrying a title link would put a second one in the tab order.
   */
  renderDraggedCard: (card: BoardCardAddress) => ReactNode
}

/**
 * The copy of a held card that follows the pointer.
 *
 * **The card itself does not move, and that is the decision this component exists to
 * carry.** The obvious way to make a card follow a pointer is a `transform` on the card,
 * and it does not work here: a card sits inside a cell that scrolls its own list and
 * windows what it holds, so translating the card moves it within a box that clips, and it
 * disappears under its own cell's edge rather than travelling across the board. So the
 * held card stays where it is, marked as held by whatever draws it, and this draws a copy
 * at the pointer. That is the arrangement dnd-kit documents and the one the board in
 * `@github-ui/workstreams-viewer` already ships.
 *
 * **Render it inside a portal.** It is `position: fixed`, so it must not end up inside
 * either of the board's scroll containers or inside anything that makes itself a
 * containing block for fixed descendants, and a caller's own CSS around the board usually
 * assumes the board is the only element in its box. There is no container prop to do it
 * here, so the layer that renders this does it: `@github-ui/board` wraps it in Primer's
 * `Portal`, which also settles the server, where a portal renders nothing at all.
 *
 * It is the one component this package renders, and it is here because `drag/dnd-kit.ts`
 * is the package's only door to the drag library: a styled layer cannot reach
 * `DragOverlay` without a dependency on the library the two-package split exists to keep
 * out. It still draws no card of its own -- the copy is entirely the caller's -- so what
 * the split protects is intact.
 *
 * **Nothing animates back on a drop.** A drop animation returns the copy to the card it
 * came from, and until a drop can move a card that journey is a lie: it would show a card
 * arriving somewhere it has not gone. `@github-ui/workstreams-viewer` turns the animation
 * off for the same reason.
 */
export function BoardDragOverlay({renderDraggedCard}: BoardDragOverlayProps): ReactNode {
  const {draggingCards} = useBoardInteraction()
  const held = draggingCards[0]

  /*
    Mounted whether or not anything is being dragged, rather than mounted when a drag
    starts. dnd-kit measures this element and takes it as the node a drag is carrying, and
    a node that appears a render after the drag began is measured against a pointer that
    has already moved. It draws nothing until there is something to draw.
  */
  return (
    <DragOverlay dropAnimation={null}>
      {held === undefined ? null : (
        /*
          What the caller drew, taken out of the accessibility tree and out of the tab
          order.

          Held here rather than asked of the caller, because it is a property of being a
          copy rather than a decision anybody renders: the card this duplicates is still in
          the document and has not moved, so a readable copy announces the same card twice,
          and a card is usually a title link, so a reachable copy is a second tab stop for
          a card that has one already.

          Both attributes rather than either. `aria-hidden` alone leaves every control in
          the copy focusable, which is worse than not hiding it at all: a tab stop with
          neither a name nor a role. `inert` does take a subtree out of the accessibility
          tree in a current browser, but it is reported unevenly, so the pair is what this
          repository uses wherever a subtree is hidden and disabled together.

          Sized to fill the overlay rather than given no box at all, and that is not a
          cosmetic choice. dnd-kit measures the overlay's single element child rather than
          the overlay itself, so this wrapper is the node it measures: `display: contents`
          reads back as 0x0, and that rect is what a drop target is tested against once
          there is one (https://github.com/github/views/issues/99). Filling the overlay
          measures the card being dragged, which is the box dnd-kit uses when there is no
          overlay at all. It changes no layout: the copy is laid out at the top of a box
          the size of the one it was already in.
        */
        <div aria-hidden="true" inert style={FILLS_THE_OVERLAY}>
          {renderDraggedCard(held)}
        </div>
      )}
    </DragOverlay>
  )
}

const FILLS_THE_OVERLAY: CSSProperties = {blockSize: '100%', inlineSize: '100%'}
